/* eslint-disable max-len */

import { BamCommercialFamilyCodesFileResponse } from '@1po/1po-bff-fe-spec/generated/backoffice/bam_commercial_family_codes/response/BamCommercialFamilyCodesFileResponse';
import { WsResponse } from '@1po/1po-bff-fe-spec/generated/common/WsResponse';
import { call, put, takeEvery } from '@redux-saga/core/effects';
import { SagaIterator } from 'redux-saga';
import {
  DELETING_FILE,
  FETCHING_FILE,
  FILE,
  FILE_DELETED,
  FILE_FETCHED,
  FILE_REPLACED,
  FILE_UPLOADED,
  NO_FILE,
  UPLOADING_FILE,
} from 'components/FileUpload/paramsByState';
import {
  sendBamCommercialFamilyCodesFileRequest,
  sendDeleteBamCommercialFamilyCodesFileRequest,
  sendGetBamCommercialFamilyCodesFileRequest,
} from 'domains/bamCommercialFamilyCodes/BamCommercialFamilyCodes.api';
import * as actions from 'domains/bamCommercialFamilyCodes/BamCommercialFamilyCodes.store';
import {
  getBamCommercialFamilyCodesFileUploadStatus,
  setBamCommercialFamilyCodesFileModalStatus,
  setBamCommercialFamilyCodesFileResponse,
  setBamCommercialFamilyCodesFileUploadStatus,
  setBamCommercialFamilyCodesSearchFileStatus,
} from 'domains/bamCommercialFamilyCodes/BamCommercialFamilyCodes.store';

import { notifyTop } from 'UI';
import { AppTranslation, FOUND, LOADING, NOT_FOUND, sagaGuard, select } from 'utils';

export function* fetchBamCommercialFamilyCodesFileRequestSaga(): SagaIterator {
  yield put(setBamCommercialFamilyCodesSearchFileStatus(LOADING));
  yield put(setBamCommercialFamilyCodesFileUploadStatus(FETCHING_FILE));
  yield put(sendGetBamCommercialFamilyCodesFileRequest());
}

export function* fetchBamCommercialFamilyCodesFileResponseSaga(
  action: WsResponse<BamCommercialFamilyCodesFileResponse>,
): SagaIterator {
  const status = yield* select(getBamCommercialFamilyCodesFileUploadStatus);
  if (status === FETCHING_FILE) {
    yield put(setBamCommercialFamilyCodesFileUploadStatus(FILE_FETCHED));
  } else {
    yield put(setBamCommercialFamilyCodesFileUploadStatus(FILE_REPLACED));
  }

  yield put(setBamCommercialFamilyCodesFileResponse(action.payload));
  yield put(setBamCommercialFamilyCodesSearchFileStatus(FOUND));
  yield put(setBamCommercialFamilyCodesFileModalStatus(FILE));
}

export function* uploadFileBamCommercialFamilyCodesRequestSaga({
  payload,
}: ReturnType<typeof actions.uploadFileBamCommercialFamilyCodesRequestSaga>): SagaIterator {
  yield put(setBamCommercialFamilyCodesFileUploadStatus(UPLOADING_FILE));
  yield put(sendBamCommercialFamilyCodesFileRequest({ ...payload }));
}

export function* uploadFileBamCommercialFamilyCodesResponseSaga(): SagaIterator {
  yield put(setBamCommercialFamilyCodesFileUploadStatus(FILE_UPLOADED));
  yield put(setBamCommercialFamilyCodesFileModalStatus(NO_FILE));
  yield put(setBamCommercialFamilyCodesSearchFileStatus(undefined));
  yield call(
    notifyTop,
    'success',
    AppTranslation.t('common.file_upload.success', 'Your file was successfully uploaded.'),
    null,
  );
}

export function* deleteFileBamCommercialFamilyCodesRequestSaga(): SagaIterator {
  yield put(setBamCommercialFamilyCodesFileUploadStatus(DELETING_FILE));
  yield put(sendDeleteBamCommercialFamilyCodesFileRequest());
}

export function* deleteFileBamCommercialFamilyCodesResponseSaga(): SagaIterator {
  yield put(setBamCommercialFamilyCodesFileResponse({ fileName: '', fileRows: [] }));
  yield put(setBamCommercialFamilyCodesSearchFileStatus(NOT_FOUND));
  yield put(setBamCommercialFamilyCodesFileUploadStatus(FILE_DELETED));
  yield put(setBamCommercialFamilyCodesFileModalStatus(NO_FILE));
}

export function* bamCommercialFamilyCodesSagas(): SagaIterator {
  yield takeEvery(
    actions.uploadFileBamCommercialFamilyCodesRequestSaga.type,
    sagaGuard(uploadFileBamCommercialFamilyCodesRequestSaga),
  );

  yield takeEvery(
    actions.uploadFileBamCommercialFamilyCodesResponseSaga.type,
    sagaGuard(uploadFileBamCommercialFamilyCodesResponseSaga),
  );

  yield takeEvery(
    actions.fetchFileBamCommercialFamilyCodesResponseSaga.type,
    sagaGuard(fetchBamCommercialFamilyCodesFileResponseSaga),
  );
  yield takeEvery(
    actions.fetchFileBamCommercialFamilyCodesRequestSaga.type,
    sagaGuard(fetchBamCommercialFamilyCodesFileRequestSaga),
  );
  yield takeEvery(
    actions.deleteFileBamCommercialFamilyCodesRequestSaga.type,
    sagaGuard(deleteFileBamCommercialFamilyCodesRequestSaga),
  );
  yield takeEvery(
    actions.deleteFileBamCommercialFamilyCodesResponseSaga.type,
    sagaGuard(deleteFileBamCommercialFamilyCodesResponseSaga),
  );
}
